import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { teamApi, type OperatorTeam, type TeamApprovalCapability, apiFetch } from '@/lib/api';
import { PageGuide } from '@/components/PageGuide';
import {
  UsersRound, Plus, Trash2, Tag, Users, ChevronRight,
  Shield, AlertTriangle, UserPlus, X,
} from 'lucide-react';

export const Route = createFileRoute('/_auth/teams')({
  component: TeamsPage,
});

interface TenantUser { id: string; email: string; name: string | null; role: string; }

/* ── Tiny colour dot for team accent ────────────────── */
function TeamDot({ color }: { color: string | null }) {
  return (
    <span style={{
      display: 'inline-block',
      width: 10, height: 10, borderRadius: '50%',
      background: color ?? '#6366f1',
      flexShrink: 0,
    }} />
  );
}

/* ── Capability pill ─────────────────────────────────── */
function CapabilityPill({ cap, onRemove }: { cap: TeamApprovalCapability; onRemove: () => void }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 20,
      background: '#6366f115', border: '1px solid #6366f130',
      fontSize: 11, color: '#a5b4fc', fontFamily: 'monospace',
    }}>
      <Tag size={9} />
      {cap.capability}
      {cap.maxValue !== null && <span style={{ opacity: 0.6 }}>≤{cap.maxValue}</span>}
      <button
        onClick={onRemove}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 0, lineHeight: 1, marginLeft: 2 }}
        title="Remove capability"
      >×</button>
    </span>
  );
}

/* ── Create team drawer ──────────────────────────────── */
function CreateTeamModal({ onClose, onCreated }: { onClose: () => void; onCreated: (t: OperatorTeam) => void }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#6366f1');
  const [threshold, setThreshold] = useState(20);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const team = await teamApi.create({ name, description, color, trafficThreshold: threshold });
      onCreated(team);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create team');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', zIndex: 40 }} />
      <form
        onSubmit={submit}
        style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '100%', maxWidth: 420, background: '#0d1022', borderLeft: '1px solid #1e2130', zIndex: 50, padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto', boxShadow: '-16px 0 60px rgba(0,0,0,0.6)', animation: 'drawerIn 0.22s cubic-bezier(0.34,1.2,0.64,1)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#e2e8f0' }}>New Team</h2>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 4 }}><X size={18} /></button>
        </div>

        {error && <div style={{ background: '#ef444415', border: '1px solid #ef444430', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#f87171' }}>{error}</div>}

        <label style={labelStyle}>Team name *
          <input required value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Rewards Team" style={{ ...inputStyle, marginTop: 6 }} />
        </label>
        <label style={labelStyle}>Description
          <input value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional" style={{ ...inputStyle, marginTop: 6 }} />
        </label>
        <label style={labelStyle}>Accent colour
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
            <input type="color" value={color} onChange={e => setColor(e.target.value)} style={{ width: 40, height: 36, borderRadius: 6, border: '1px solid #1e2130', background: 'transparent', cursor: 'pointer', padding: 2 }} />
            <span style={{ fontSize: 12, color: '#64748b', fontFamily: 'monospace' }}>{color}</span>
          </div>
        </label>
        <label style={labelStyle}>Traffic threshold (open conversations)
          <input type="number" min={1} max={9999} value={threshold} onChange={e => setThreshold(Number(e.target.value))} style={{ ...inputStyle, marginTop: 6 }} />
        </label>

        <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
          <button type="button" onClick={onClose} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1px solid #2d3148', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
          <button type="submit" disabled={saving || !name.trim()} style={{ flex: 2, padding: '10px 0', borderRadius: 8, border: 'none', background: '#6366f1', color: '#fff', cursor: saving || !name.trim() ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: saving || !name.trim() ? 0.6 : 1 }}>
            {saving ? 'Creating…' : 'Create Team'}
          </button>
        </div>
      </form>
      <style>{`@keyframes drawerIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
    </>
  );
}

/* ── Add Member modal ────────────────────────────────── */
function AddMemberModal({ teamId, existingMemberIds, onClose, onAdded }: {
  teamId: string;
  existingMemberIds: string[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const { data: users = [] } = useQuery<TenantUser[]>({
    queryKey: ['tenant-users'],
    queryFn: () => apiFetch('/v1/me/users'),
  });
  const [selectedUserId, setSelectedUserId] = useState('');
  const [role, setRole] = useState<'MEMBER' | 'LEAD'>('MEMBER');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const available = users.filter(u => !existingMemberIds.includes(u.id));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedUserId) return;
    setSaving(true);
    try {
      await teamApi.addMember(teamId, selectedUserId, role);
      onAdded();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to add member');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', zIndex: 40 }} />
      <form
        onSubmit={submit}
        style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '100%', maxWidth: 400, background: '#0d1022', border: '1px solid #1e2130', borderRadius: 16, zIndex: 50, padding: '24px', display: 'flex', flexDirection: 'column', gap: 16, boxShadow: '0 24px 80px rgba(0,0,0,0.7)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#e2e8f0' }}>Add Member</h3>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 4 }}><X size={16} /></button>
        </div>

        {error && <div style={{ background: '#ef444415', border: '1px solid #ef444430', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#f87171' }}>{error}</div>}

        {available.length === 0 ? (
          <p style={{ margin: 0, fontSize: 13, color: '#64748b', textAlign: 'center', padding: '16px 0' }}>All users are already members of this team.</p>
        ) : (
          <>
            <label style={labelStyle}>User *
              <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)} style={{ ...inputStyle, marginTop: 6 }} required>
                <option value="">Select a user…</option>
                {available.map(u => (
                  <option key={u.id} value={u.id}>{u.name ?? u.email} ({u.role})</option>
                ))}
              </select>
            </label>
            <label style={labelStyle}>Role
              <select value={role} onChange={e => setRole(e.target.value as 'MEMBER' | 'LEAD')} style={{ ...inputStyle, marginTop: 6 }}>
                <option value="MEMBER">Member</option>
                <option value="LEAD">Lead</option>
              </select>
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={onClose} style={{ flex: 1, padding: '9px 0', borderRadius: 8, border: '1px solid #2d3148', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
              <button type="submit" disabled={saving || !selectedUserId} style={{ flex: 2, padding: '9px 0', borderRadius: 8, border: 'none', background: '#6366f1', color: '#fff', cursor: saving || !selectedUserId ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: saving || !selectedUserId ? 0.6 : 1 }}>
                {saving ? 'Adding…' : 'Add Member'}
              </button>
            </div>
          </>
        )}
      </form>
    </>
  );
}

/* ── Main Page ───────────────────────────────────────── */
export default function TeamsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState<OperatorTeam | null>(null);
  const [newCapKey, setNewCapKey] = useState('');
  const [capInputFocused, setCapInputFocused] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);

  const { data: teams = [], isLoading } = useQuery({
    queryKey: ['teams'],
    queryFn: teamApi.list,
  });

  const addCap = useMutation({
    mutationFn: ({ teamId, capability }: { teamId: string; capability: string }) =>
      teamApi.addCapability(teamId, capability),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['teams'] }); setNewCapKey(''); },
  });

  const removeCap = useMutation({
    mutationFn: ({ teamId, capability }: { teamId: string; capability: string }) =>
      teamApi.removeCapability(teamId, capability),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['teams'] }),
  });

  function handleCreated(team: OperatorTeam) {
    qc.invalidateQueries({ queryKey: ['teams'] });
    setShowCreate(false);
    setSelected(team);
  }

  // Sync selected with fresh data
  const selectedFresh = selected ? teams.find(t => t.id === selected.id) ?? selected : null;
  const existingMemberIds = (selectedFresh?.members ?? []).map(m => m.userId);

  return (
    <div className="page">
      <PageGuide pageKey="teams" />

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#e2e8f0' }}>{t('nav.teams')}</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Operator teams with approval capabilities and member assignments</p>
        </div>
        <button
          id="create-team-btn"
          onClick={() => setShowCreate(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', borderRadius: 9, border: 'none', background: 'var(--color-primary)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, boxShadow: '0 2px 12px var(--color-primary-glow)' }}
        >
          <Plus size={15} /> New Team
        </button>
      </div>

      {/* ── Content grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 16, alignItems: 'start' }}>

        {/* Team list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {isLoading && <p style={{ fontSize: 13, color: '#64748b' }}>Loading…</p>}
          {!isLoading && teams.length === 0 && (
            <div style={{ textAlign: 'center', padding: '32px 16px', color: '#475569', fontSize: 13 }}>
              <UsersRound size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
              <p style={{ margin: 0 }}>No teams yet. Create your first team to get started.</p>
            </div>
          )}

          {teams.map(team => (
            <button
              key={team.id}
              id={`team-${team.id}`}
              onClick={() => setSelected(team)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
                borderRadius: 10, border: `1px solid ${selectedFresh?.id === team.id ? '#6366f150' : 'var(--border, #2d3148)'}`,
                background: selectedFresh?.id === team.id ? '#6366f110' : 'var(--surface-2, #1e2130)',
                cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
              }}
            >
              <TeamDot color={team.color} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {team.name}
                </div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                  {team._count?.members ?? 0} members · {team._count?.hitlRequests ?? 0} pending
                </div>
              </div>
              <ChevronRight size={14} style={{ opacity: 0.3 }} />
            </button>
          ))}
        </div>

        {/* ── Team detail ── */}
        {selectedFresh ? (
          <div style={{
            borderRadius: 12, border: '1px solid var(--border, #2d3148)',
            background: 'var(--surface-2, #1e2130)', padding: 24,
            display: 'flex', flexDirection: 'column', gap: 24,
          }}>
            {/* Team header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <TeamDot color={selectedFresh.color} />
              <div>
                <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#e2e8f0' }}>{selectedFresh.name}</h2>
                {selectedFresh.description && (
                  <p style={{ margin: '2px 0 0', fontSize: 12, color: '#64748b' }}>{selectedFresh.description}</p>
                )}
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                {selectedFresh.trafficThreshold && (
                  <span style={{ fontSize: 11, color: '#64748b', background: '#1e2130', border: '1px solid #2d3148', borderRadius: 6, padding: '3px 9px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <AlertTriangle size={10} />
                    Threshold: {selectedFresh.trafficThreshold}
                  </span>
                )}
              </div>
            </div>

            {/* ── Capabilities ── */}
            <section>
              <h3 style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Shield size={12} /> Approval Capabilities
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                {(selectedFresh.capabilities ?? []).length === 0 && (
                  <span style={{ fontSize: 12, color: '#475569' }}>No capabilities yet — add one below.</span>
                )}
                {(selectedFresh.capabilities ?? []).map(cap => (
                  <CapabilityPill
                    key={cap.id}
                    cap={cap}
                    onRemove={() => removeCap.mutate({ teamId: selectedFresh.id, capability: cap.capability })}
                  />
                ))}
              </div>
              {(() => {
                const currentCaps = new Set((selectedFresh.capabilities ?? []).map(c => c.capability));
                const normalizedInput = newCapKey.trim();
                const isDuplicate = currentCaps.has(normalizedInput);
                // Allow trailing dot while typing, but require complete segments on submit
                const isValidWhileTyping = normalizedInput.length === 0 || /^[a-z0-9]+([._][a-z0-9]+)*\.?$/.test(normalizedInput);
                const isComplete = /^[a-z0-9]+([._][a-z0-9]+)*$/.test(normalizedInput);

                // Collect all unique capabilities with team names for context
                const capTeamMap = new Map<string, string[]>();
                teams.forEach(t => {
                  (t.capabilities ?? []).forEach((c: TeamApprovalCapability) => {
                    const list = capTeamMap.get(c.capability) ?? [];
                    list.push(t.name);
                    capTeamMap.set(c.capability, list);
                  });
                });
                const allCaps = [...capTeamMap.keys()].filter(c => !currentCaps.has(c));
                const suggestions = normalizedInput
                  ? allCaps.filter(c => c.startsWith(normalizedInput) || c.startsWith(normalizedInput.replace(/\.$/, '')))
                  : allCaps;
                const showDropdown = capInputFocused && suggestions.length > 0;

                function normalizeCap(raw: string): string {
                  return raw
                    .toLowerCase()
                    .replace(/\s+/g, '.')     // spaces → dots
                    .replace(/\.{2,}/g, '.')  // collapse consecutive dots
                    .replace(/[^a-z0-9._]/g, ''); // strip invalid chars
                }

                function selectSuggestion(cap: string) {
                  if (!selectedFresh) return;
                  addCap.mutate({ teamId: selectedFresh.id, capability: cap });
                  setNewCapKey('');
                  setCapInputFocused(false);
                }

                return (
                  <>
                    <form
                      onSubmit={e => {
                        e.preventDefault();
                        if (normalizedInput && !isDuplicate && isComplete) {
                          addCap.mutate({ teamId: selectedFresh.id, capability: normalizedInput });
                          setNewCapKey('');
                        }
                      }}
                      style={{ display: 'flex', gap: 8, position: 'relative' }}
                    >
                      <div style={{ flex: 1, position: 'relative' }}>
                        <input
                          id="new-capability-key"
                          value={newCapKey}
                          onChange={e => setNewCapKey(normalizeCap(e.target.value))}
                          onFocus={() => setCapInputFocused(true)}
                          onBlur={() => setTimeout(() => setCapInputFocused(false), 150)}
                          placeholder="e.g. reward.increase"
                          autoComplete="off"
                          style={{
                            width: '100%', padding: '7px 11px', borderRadius: 7,
                            border: `1px solid ${isDuplicate ? '#ef4444' : !isValidWhileTyping ? '#f59e0b' : capInputFocused ? '#6366f1' : 'var(--border, #2d3148)'}`,
                            background: 'var(--surface-1, #161927)',
                            color: '#e2e8f0', fontSize: 12, fontFamily: 'monospace',
                            outline: 'none', transition: 'border-color 0.15s',
                          }}
                        />

                        {/* Autocomplete dropdown */}
                        {showDropdown && (
                          <div style={{
                            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                            marginTop: 4, borderRadius: 8, overflow: 'hidden',
                            background: '#161927', border: '1px solid #2d3148',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                            maxHeight: 180, overflowY: 'auto',
                          }}>
                            {suggestions.slice(0, 8).map(cap => {
                              const usedBy = capTeamMap.get(cap) ?? [];
                              return (
                                <button
                                  key={cap}
                                  type="button"
                                  onMouseDown={e => { e.preventDefault(); selectSuggestion(cap); }}
                                  style={{
                                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '7px 11px', border: 'none', background: 'transparent',
                                    cursor: 'pointer', textAlign: 'left', transition: 'background 0.1s',
                                  }}
                                  onMouseEnter={e => { e.currentTarget.style.background = '#6366f115'; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                >
                                  <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#a5b4fc' }}>{cap}</span>
                                  <span style={{ fontSize: 10, color: '#475569', flexShrink: 0, marginLeft: 8 }}>
                                    {usedBy.join(', ')}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <button
                        type="submit"
                        disabled={!normalizedInput || isDuplicate || !isComplete || addCap.isPending}
                        title="Add capability"
                        style={{ padding: '7px 14px', borderRadius: 7, border: 'none', background: '#6366f1', color: '#fff', cursor: normalizedInput && !isDuplicate && isComplete ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 600, opacity: normalizedInput && !isDuplicate && isComplete ? 1 : 0.5 }}
                      >
                        <Plus size={13} />
                      </button>
                    </form>
                    {isDuplicate && <p style={{ margin: '4px 0 0', fontSize: 11, color: '#f87171' }}>This team already has this capability.</p>}
                    {!isValidWhileTyping && normalizedInput && <p style={{ margin: '4px 0 0', fontSize: 11, color: '#f59e0b' }}>Use dot notation: lowercase letters, numbers, dots, underscores only.</p>}
                    {addCap.isError && <p style={{ margin: '6px 0 0', fontSize: 11, color: '#f87171' }}>Failed to add capability. Try again.</p>}
                  </>
                );
              })()}
            </section>

            {/* ── Members ── */}
            <section>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <h3 style={{ margin: 0, fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Users size={12} /> Members
                </h3>
                <button
                  id="add-member-btn"
                  onClick={() => setShowAddMember(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 7, border: '1px solid #6366f130', background: '#6366f110', color: '#818cf8', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                >
                  <UserPlus size={12} /> Add Member
                </button>
              </div>
              {(selectedFresh.members ?? []).length === 0 ? (
                <p style={{ fontSize: 12, color: '#475569', margin: 0 }}>No members assigned to this team yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {(selectedFresh.members ?? []).map(m => (
                    <div key={m.id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 12px', borderRadius: 8,
                      background: 'var(--surface-1, #161927)', fontSize: 12, color: '#cbd5e1',
                    }}>
                      <span>{m.user?.name ?? m.user?.email ?? m.userId}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{
                          fontSize: 10, padding: '2px 7px', borderRadius: 10,
                          background: m.role === 'LEAD' ? '#f59e0b20' : '#6366f115',
                          color: m.role === 'LEAD' ? '#fbbf24' : '#818cf8', fontWeight: 700,
                        }}>
                          {m.role}
                        </span>
                        <button
                          onClick={() => teamApi.removeMember(selectedFresh.id, m.userId).then(() => qc.invalidateQueries({ queryKey: ['teams'] }))}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 0 }}
                          title="Remove member"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        ) : (
          <div style={{
            borderRadius: 12, border: '1px dashed var(--border, #2d3148)',
            padding: '48px 32px', textAlign: 'center', color: '#475569', fontSize: 13,
          }}>
            <UsersRound size={32} style={{ opacity: 0.2, marginBottom: 12 }} />
            <p style={{ margin: 0 }}>Select a team to view its members and capabilities.</p>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateTeamModal
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}

      {showAddMember && selectedFresh && (
        <AddMemberModal
          teamId={selectedFresh.id}
          existingMemberIds={existingMemberIds}
          onClose={() => setShowAddMember(false)}
          onAdded={() => qc.invalidateQueries({ queryKey: ['teams'] })}
        />
      )}
    </div>
  );
}

const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 0 };
const inputStyle: React.CSSProperties = { width: '100%', padding: '9px 12px', background: '#0f1221', border: '1px solid #1e2130', borderRadius: 8, color: '#e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box' };
